/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Link, useParams } from 'react-router-dom'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { Search } from 'components/Input/Search'
import TabsNavigation from 'components/TabsNavigation'
import Table, { TableProps, FooterWrapper, PaginationTake, PaginationPage } from 'components/Table'
import { TabsItem } from 'types/tabsItem'

import { PlatformSlug } from 'constants/consts'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { AddPartnershipModal } from './AddPartnershipModal/AddPartnershipModal'
import * as classes from './styles'
import { useFindAllInstitutionalCodesQuery, useFindSchoolsByPlatformQuery } from './hooks'

type UniversityData = {
  id: string
  university: string
  cohort: string
  facultyMembersCount: number
  studentsCount: number
  remainingStudentCodesCount: number
}

const tabs: TabsItem[] = [{ id: 'all', title: 'All' }]

export const InstitutionalCode = withPlatformCheck(() => {
  const { getLink } = useLink()
  const [activeTab, setActiveTab] = useState<TabsItem>(tabs[0])
  const params = useParams<{ platform: PlatformSlug }>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { selectedPlatform } = usePlatformsAndSubjects()
  const platformName = selectedPlatform?.title ? selectedPlatform.title : undefined
  const platformId = selectedPlatform?.id ?? ''

  const schoolsByPlatformQuery = useFindSchoolsByPlatformQuery({
    skip: !platformId,
    variables: {
      platformId: platformId,
    },
  })

  const findAllInstitutionalCodesQuery = useFindAllInstitutionalCodesQuery()

  const isDataLoaded =
    !!schoolsByPlatformQuery.data?.findSchoolListByPlatform?.schoolArray && !!findAllInstitutionalCodesQuery.items

  useEffect(() => {
    if (platformId) {
      findAllInstitutionalCodesQuery.onFilterChange('platform', { id: platformId })
    }
  }, [platformId, findAllInstitutionalCodesQuery.onFilterChange])

  const handleAddPartnership = () => {
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const schoolsMap = useMemo(() => {
    const map = new Map<string, string>()
    schoolsByPlatformQuery.data?.findSchoolListByPlatform?.schoolArray?.forEach((school) => {
      map.set(school.key, school.value)
    })
    return map
  }, [schoolsByPlatformQuery.data?.findSchoolListByPlatform?.schoolArray])

  const universities: UniversityData[] = useMemo(() => {
    if (!findAllInstitutionalCodesQuery.items || !schoolsMap) {
      return []
    }
    return findAllInstitutionalCodesQuery.items.map((item) => ({
      id: item.id,
      university: schoolsMap.get(item.university) ?? item.university,
      cohort: item.cohort,
      facultyMembersCount: item.facultyMembersCount ?? 0,
      studentsCount: item.studentsCount ?? 0,
      remainingStudentCodesCount: item.remainingUserCodesCount ?? 0,
    }))
  }, [findAllInstitutionalCodesQuery.items, schoolsMap])

  const columns: TableProps<UniversityData>['columns'] = useMemo(
    () => [
      {
        title: 'University',
        renderCell: (university) => <Typography variant="body2">{university.university}</Typography>,
        flex: '2',
      },
      {
        title: 'Cohort',
        renderCell: (university) => {
          const match = university.cohort.match(/\d{4}$/)
          const year = match ? match[0] : university.cohort
          return <Typography variant="body2">{year}</Typography>
        },
        flex: '1',
      },
      {
        title: '# of Faculty Members',
        renderCell: (university) => <Typography variant="body2">{university.facultyMembersCount}</Typography>,
        flex: '1',
        orderField: 'facultyMembersCount',
      },
      {
        title: '# of Students',
        renderCell: (university) => <Typography variant="body2">{university.studentsCount}</Typography>,
        flex: '1',
        orderField: 'studentsCount',
      },
      {
        title: '# of Remaining Student Codes',
        renderCell: (university) => <Typography variant="body2">{university.remainingStudentCodesCount}</Typography>,
        flex: '1',
        orderField: 'remainingUserCodesCount',
      },
      {
        title: '',
        renderCell: (params) => (
          <IconButton
            component={Link}
            size="small"
            css={classes.actionButton}
            to={getLink(cmsRoutes.INSTITUTIONAL_CODES_EDIT, { institutionalCodeId: params.id })}
          >
            <EditIcon />
          </IconButton>
        ),
        flex: '0 1 64px',
      },
    ],
    [getLink]
  )

  return (
    <div css={classes.container}>
      <Typography
        variant="title3"
        sx={{
          fontWeight: '700',
        }}
      >
        Institutional Codes
      </Typography>

      <div css={classes.filtersSection}>
        <TabsNavigation tabs={tabs} value={activeTab} onChange={setActiveTab} />
        <Search {...findAllInstitutionalCodesQuery.search} />
      </div>

      <div css={classes.tableSection}>
        <Table
          columns={columns}
          items={universities}
          isNotLoaded={!isDataLoaded}
          order={findAllInstitutionalCodesQuery.order.value}
          onOrder={findAllInstitutionalCodesQuery.order.onChange}
          wrapperMinWidth={1000}
        />

        <Button
          variant="outlined"
          color="blue"
          size="large"
          onClick={handleAddPartnership}
          css={classes.addPartnershipButton}
        >
          Add Partnership <AddRoundedIcon />
        </Button>
      </div>

      <FooterWrapper css={classes.footerWrapper}>
        <PaginationTake {...findAllInstitutionalCodesQuery.pagination} />
        <PaginationPage
          totalCount={findAllInstitutionalCodesQuery.count ?? 0}
          {...findAllInstitutionalCodesQuery.pagination}
        />
      </FooterWrapper>

      <AddPartnershipModal
        open={isModalOpen && !!params.platform}
        onClose={handleModalClose}
        platformName={platformName}
        selectedPlatform={selectedPlatform}
      />
    </div>
  )
})
